/**
 * @voltxampere/engine — Gear-2 / BDF2 companion models (SPECS §3.8)
 *
 * Gear-2 is a 2nd-order Backward Differentiation Formula. Like BE
 * it is L-stable, so it does not ring on stiff problems the way
 * TRAP can; like TRAP it converges as 2nd order in h. The trade-off
 * is that BDF2 needs **two** previous points (`x(t−h_n)` and
 * `x(t−h_n−h_{n−1})`) — on the first transient step the solver
 * falls back to BE.
 *
 * **Variable-step** form (the one this file ships) accommodates the
 * SPECS §3.9 adaptive-step controller, which routinely changes the
 * step size between accepted snapshots. Letting `ω = h_n / h_{n−1}`
 * be the step ratio:
 *
 *   y'(t_n) ≈ α_n · y_n + α_{n−1} · y_{n−1} + α_{n−2} · y_{n−2}
 *   α_n     =  (1 + 2ω) / (h_n · (1 + ω))
 *   α_{n−1} = −(1 + ω)  /  h_n
 *   α_{n−2} =   ω²      / (h_n · (1 + ω))
 *
 * Substituting `i = C · y'` for the capacitor (and the dual for the
 * inductor) gives Geq / Ieq in Norton form:
 *
 *   Geq = C · α_n
 *   Ieq = C · (α_{n−1}·V(t−h_n) + α_{n−2}·V(t−h_n−h_{n−1}))
 *
 * In the uniform-step degenerate case (`ω = 1`, `h_n = h`) the
 * coefficients collapse to the SPECS §3.8 textbook formula:
 *
 *   α_n = 3/(2h),  α_{n−1} = −2/h,  α_{n−2} = 1/(2h)
 *
 * which the unit tests below verify directly.
 *
 * BDF2 unlike TRAP does NOT need `i_C(t−h)` — both companions read
 * only from the V/I history vectors. That makes the storage burden
 * lighter than TRAP for circuits where the extra L-stability is
 * worth losing trapezoidal symmetry.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/**
 * Variable-step BDF2 coefficient triple. See file-level docstring
 * for the closed-form expressions.
 */
function bdf2Coefficients(h, hPrev) {
    const omega = h / hPrev;
    const onePlusOmega = 1 + omega;
    return {
        alphaN: (1 + 2 * omega) / (h * onePlusOmega),
        alphaNm1: -onePlusOmega / h,
        alphaNm2: (omega * omega) / (h * onePlusOmega),
    };
}
/**
 * Capacitor companion under Gear-2 (BDF2).
 *
 * @param C            capacitance (> 0).
 * @param h            current step size `h_n` (> 0).
 * @param hPrev        previous accepted step's size `h_{n−1}` (> 0).
 * @param VCPrev       capacitor voltage at `t − h_n`.
 * @param VCPrevPrev   capacitor voltage at `t − h_n − h_{n−1}`.
 */
export function capacitorGear2(C, h, hPrev, VCPrev, VCPrevPrev) {
    if (!(C > 0) || !Number.isFinite(C)) {
        throw new ContractViolationError(`capacitorGear2: C must be a positive finite number, got ${String(C)}`);
    }
    if (!(h > 0) || !Number.isFinite(h)) {
        throw new ContractViolationError(`capacitorGear2: h must be a positive finite number, got ${String(h)}`);
    }
    if (!(hPrev > 0) || !Number.isFinite(hPrev)) {
        throw new ContractViolationError(`capacitorGear2: hPrev must be a positive finite number, got ${String(hPrev)}`);
    }
    if (!Number.isFinite(VCPrev) || !Number.isFinite(VCPrevPrev)) {
        throw new ContractViolationError(`capacitorGear2: history values must be finite, got VCPrev=${String(VCPrev)}, VCPrevPrev=${String(VCPrevPrev)}`);
    }
    const { alphaN, alphaNm1, alphaNm2 } = bdf2Coefficients(h, hPrev);
    const Geq = C * alphaN;
    const Ieq = C * (alphaNm1 * VCPrev + alphaNm2 * VCPrevPrev);
    return { Geq, Ieq };
}
/**
 * Inductor companion under Gear-2 (BDF2). Same variable-step
 * formula as the capacitor — dual variables (V↔I, C↔L).
 */
export function inductorGear2(L, h, hPrev, ILPrev, ILPrevPrev) {
    if (!(L > 0) || !Number.isFinite(L)) {
        throw new ContractViolationError(`inductorGear2: L must be a positive finite number, got ${String(L)}`);
    }
    if (!(h > 0) || !Number.isFinite(h)) {
        throw new ContractViolationError(`inductorGear2: h must be a positive finite number, got ${String(h)}`);
    }
    if (!(hPrev > 0) || !Number.isFinite(hPrev)) {
        throw new ContractViolationError(`inductorGear2: hPrev must be a positive finite number, got ${String(hPrev)}`);
    }
    if (!Number.isFinite(ILPrev) || !Number.isFinite(ILPrevPrev)) {
        throw new ContractViolationError(`inductorGear2: history values must be finite, got ILPrev=${String(ILPrev)}, ILPrevPrev=${String(ILPrevPrev)}`);
    }
    const { alphaN, alphaNm1, alphaNm2 } = bdf2Coefficients(h, hPrev);
    const Geq = L * alphaN;
    const Ieq = L * (alphaNm1 * ILPrev + alphaNm2 * ILPrevPrev);
    return { Geq, Ieq };
}
//# sourceMappingURL=gear-2.js.map